import { isEmpty } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import useFriends from '@/hooks/contacts/use-friends';
import type { SearchMessageResult } from '@/hooks/search/use-search-messages';
import ROUTES from '@/routes';
import { setPanel } from '@/stores/layout-status';
import { getDateFormatKey } from '@/utils/datetime';
import cn from '@/utils/merge-class-names';
import { generateSessionKey } from '@/utils/session';
import SearchListItem from './search-list-item';
import SessionLink from './session-link';

interface Props {
  results: SearchMessageResult[];
  className?: string;
  keyword?: string;
}

export default function SearchMessageResultList({ results, className, keyword = '' }: Props) {
  const { t } = useTranslation();
  const { friends } = useFriends();
  if (isEmpty(results)) return null;

  return (
    <div className={cn('group/message', className)}>
      <h4 className='flex h-6 mb-2 group-first/message:mt-1 items-center px-5 text-body1 font-bold'>
        {t('search.chats')}
      </h4>

      {results.map(({ ChatSessionId, GroupId, DisplayImg, SessionName, SearchResultDetails, SessionNameUserId }, i) => {
        const isGroup = Boolean(GroupId);
        const title = isGroup ? SessionName : friends[SessionNameUserId]?.RemarkName || SessionName;
        const key = generateSessionKey(isGroup ? 'group' : 'session', GroupId || ChatSessionId);

        const content = (
          <SearchListItem
            avatarImageUrl={DisplayImg}
            title={title}
            highlight={keyword}
            highlightField='description'
            description={
              SearchResultDetails.length > 1
                ? t('search.related-chat-record', { count: SearchResultDetails.length })
                : (SearchResultDetails[0]?.Message ?? '')
            }
            suffix={
              <span className='whitespace-nowrap text-body2 text-base-content/50'>
                {getDateFormatKey(SearchResultDetails[0]?.CreateTime, 'session', t)}
              </span>
            }
          />
        );

        if (SearchResultDetails.length === 1)
          return (
            <SessionLink
              key={`${key}-${i}`}
              messageId={SearchResultDetails[0]?.Id}
              {...(isGroup ? { groupId: GroupId } : { sessionId: ChatSessionId })}>
              {content}
            </SessionLink>
          );

        return (
          <Link
            key={`${key}-${i}`}
            to={ROUTES.SEARCH.BY_SESSION.$buildPath({ params: { session: key }, searchParams: { keyword } })}
            onClick={() => {
              setPanel('search-by-session');
            }}>
            {content}
          </Link>
        );
      })}
    </div>
  );
}
